export const formatCompanyNameFromDomain = (domain: string): string => {
  if (!domain) return 'New Company';
  const name = domain.split('.')[0];
  const formattedName = name.charAt(0).toUpperCase() + name.slice(1);
  return `${formattedName}`;
};

export const getDomainFromEmail = (email: string): string => {
    if (!email) return '';
    return email.substring(email.lastIndexOf('@') + 1);
};

/**
 * Compact currency for stat cards: THREE significant figures, trailing zeros
 * trimmed, a suffix at every magnitude. One rule for every card so the same
 * dashboard never shows "$700.00K" beside "$700,000.00" (it did until 2026-10-07).
 *
 * Examples:
 *   formatCompactCurrency(122400000000) → "$1.22B"
 *   formatCompactCurrency(9486000000)   → "$94.9M"
 *   formatCompactCurrency(70000000)     → "$700K"
 *   formatCompactCurrency(1234000)      → "$12.3K"
 *   formatCompactCurrency(150000)       → "$1.5K"
 *   formatCompactCurrency(85000)        → "$850"
 *   formatCompactCurrency(99999999)     → "$1M"   (rounds first, then picks the unit; never "$1000K")
 *   formatCompactCurrency(-70000000)    → "-$700K"
 *   formatCompactCurrency(0)            → "$0"
 *   formatCompactCurrency(null)         → "$0.00" (empty state, unchanged)
 *
 * The exact figure belongs in the card tooltip (StatCard `valueTitle`), not here.
 *
 * @param cents - Amount in integer cents
 * @returns Compact string with K/M/B suffix
 */
export const formatCompactCurrency = (cents: number | null | undefined): string => {
    if (cents == null) return '$0.00';
    const dollars = Number(cents) / 100;
    if (Number.isNaN(dollars)) return '$0.00';

    const isNegative = dollars < 0;
    const abs = Math.abs(dollars);
    const sign = isNegative ? '-' : '';

    // 3 significant figures for a mantissa in [1, 1000), trailing zeros trimmed.
    const sig3 = (n: number): string => {
        const fixed = n >= 100 ? n.toFixed(0) : n >= 10 ? n.toFixed(1) : n.toFixed(2);
        return fixed.includes('.') ? fixed.replace(/0+$/, '').replace(/\.$/, '') : fixed;
    };

    const units: Array<[number, string]> = [
        [1_000_000_000, 'B'],
        [1_000_000, 'M'],
        [1_000, 'K'],
    ];
    for (let i = 0; i < units.length; i++) {
        const [size, suffix] = units[i];
        if (abs < size) continue;
        let mantissa = sig3(abs / size);
        // Rounding carried into the next unit ($999,999 → "1000K"): re-express there.
        if (Number(mantissa) >= 1000 && i > 0) {
            const [bigger, biggerSuffix] = units[i - 1];
            mantissa = sig3(abs / bigger);
            return `${sign}$${mantissa}${biggerSuffix}`;
        }
        return `${sign}$${mantissa}${suffix}`;
    }

    // Below $1,000: whole dollars. $999.50 rounds to $1,000 and becomes "$1K".
    const whole = Math.round(abs);
    if (whole >= 1000) return `${sign}$1K`;
    if (whole === 0) return '$0';
    return `${sign}$${whole}`;
};

/**
 * 🔴 CRITICAL: Format integer cents as currency with shorthand (K, M, B)
 *
 * Platform Standard (CLAUDE.md:212): Backend stores monetary values as integer cents.
 * Frontend MUST divide by 100 before display.
 *
 * Used by: DashboardPage, OfficeDashboardPage, stat cards, reports
 *
 * Delegates to formatCompactCurrency (three significant figures) so every
 * card follows one rule. Kept under its original name for the 19 call sites.
 *
 * Examples:
 *   formatCurrencyShorthand(123456) → "$1.23K"
 *   formatCurrencyShorthand(123456000) → "$1.23M"
 *   formatCurrencyShorthand(70000000) → "$700K"
 *   formatCurrencyShorthand(5000) → "$50"
 *
 * @param cents - Amount in integer cents (e.g., 123456)
 * @returns Formatted string with K/M/B suffix
 */
export const formatCurrencyShorthand = (cents: number | null | undefined): string =>
    formatCompactCurrency(cents);

/**
 * 🔴 CRITICAL: Format integer cents as full currency amount (no decimals)
 *
 * Used by: Stat cards, tables, detailed views, export functions
 *
 * Examples:
 *   formatCurrency(123456) → "$1,235"
 *   formatCurrency(5000) → "$50"
 *   formatCurrency(0) → "$0"
 *
 * @param cents - Amount in integer cents (e.g., 123456)
 * @returns Formatted AUD currency string (whole dollars, no cents)
 */
export const formatCurrency = (cents: number | null | undefined): string => {
    if (cents == null) return '$0';

    // ✅ CRITICAL: Divide by 100 to convert cents → dollars
    return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(cents / 100);
};

/**
 * Format dollar amount (already converted from cents)
 * Use for BigQuery results that already divide by 100
 *
 * Examples:
 *   formatDollars(1234.56) → "$1,234.56"
 *   formatDollars(50.00) → "$50.00"
 *   formatDollars(0) → "$0.00"
 *
 * @param dollars - Amount already in dollars (NOT cents)
 * @returns Formatted AUD currency string
 */
export const formatDollars = (dollars: number | null | undefined): string => {
    if (dollars == null) return '$0.00';
    return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(dollars);  // NO division - already in dollars
};

/**
 * 🔴 CRITICAL: Format loan book value (in integer cents)
 *
 * Used by: Loan book displays, settlement values
 *
 * Delegates to formatCompactCurrency (three significant figures). Until
 * 2026-10-07 this printed "$700,000.00" below $1M while the commission cards
 * beside it printed "$700.00K"; both now read "$700K".
 *
 * Examples:
 *   formatLoanBook(123456000000) → "$1.23B"
 *   formatLoanBook(123456000) → "$1.23M"
 *   formatLoanBook(70000000) → "$700K"
 *   formatLoanBook(123456) → "$1.23K"
 *
 * @param cents - Amount in integer cents
 * @returns Formatted string with K/M/B suffix
 */
export const formatLoanBook = (cents: number | null | undefined): string =>
    formatCompactCurrency(cents);

/**
 * Format plain number (non-monetary)
 *
 * Used by: Record counts, percentages, etc.
 *
 * @param value - Numeric value
 * @returns Formatted string with thousand separators
 */
export const formatNumber = (value: number): string => {
  const num = Number(value);
  if (Number.isNaN(num)) return '0';
  return num.toLocaleString('en-US');
};

/**
 * Format dollar amount with shorthand (already converted from cents)
 * Use for BigQuery results that already divide by 100, in stat cards
 *
 * Examples:
 *   formatDollarsShorthand(1234567890.00) → "$1.23B"
 *   formatDollarsShorthand(1234567.00) → "$1.23M"
 *   formatDollarsShorthand(50000.00) → "$50.00K"
 *   formatDollarsShorthand(1234.56) → "$1,234.56"
 *
 * @param dollars - Amount already in dollars (NOT cents)
 * @returns Formatted string with K/M/B suffix
 */
export const formatDollarsShorthand = (dollars: number | null | undefined): string => {
    if (dollars == null) return '$0.00';

    const num = parseFloat(String(dollars));
    if (isNaN(num)) return '$0.00';

    // Handle negatives separately
    const isNegative = num < 0;
    const absNum = Math.abs(num);

    let formatted: string;

    // Handle billions (1,000,000,000+)
    if (absNum >= 1000000000) {
        formatted = `$${(absNum / 1000000000).toFixed(2)}B`;
    }
    // Handle millions (1,000,000+)
    else if (absNum >= 1000000) {
        formatted = `$${(absNum / 1000000).toFixed(2)}M`;
    }
    // Handle thousands (10,000+) - only show K for larger amounts
    else if (absNum >= 10000) {
        formatted = `$${(absNum / 1000).toFixed(1)}K`;
    }
    // Below $10,000: show full amount
    else {
        formatted = `$${absNum.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })}`;
    }

    return isNegative ? `-${formatted}` : formatted;
};